import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import type { JobPosition, JobPosting, Application, Channel, PositionVersion, TeamMember, CompanyTeam, AiChatMessage, AiChatRequest, AiAutoFillSuggestions } from '@/types';
import { useAiChatStore, isSessionExpired, EMPTY_JOB_POSITION_SESSION, EMPTY_SESSION } from '@/stores/aiChatStore';
import { ApiError } from '@/services/httpClient';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import { buildApplicationsContext, buildCurrentPositionContext } from '@/lib/aiContext';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { setPositionCache, setPositionsCache } from '@/lib/recruiterCache';
import { queryKeys } from '@/lib/queryKeys';
import {
  useCompanyTeamsQuery,
  usePositionApplicationsFromCache,
  usePositionPostingsQuery,
  usePositionQuery,
  usePositionsQuery,
} from '@/hooks/useRecruiterListQueries';
import { normalizeJobLocation } from '@/lib/utils';

const CHANNELS: Channel[] = ['LinkedIn', 'Company Website', 'Indeed', 'Glassdoor', 'InfoJobs', 'Referral', 'Internal', 'Other'];

type FormContextDelta = NonNullable<NonNullable<AiChatRequest['context']>['formContext']>;

function buildFormContextDelta(
  editData: Partial<JobPosition>,
  knownFields: string[]
): FormContextDelta {
  const delta: Record<string, unknown> = {};
  const trackableFields = [
    'title', 'description', 'seniority', 'contractType',
    'workMode', 'location', 'salaryMin', 'salaryMax', 'salaryCurrency'
  ];
  for (const field of trackableFields) {
    const value = editData[field as keyof JobPosition];
    if (value !== undefined && value !== null && value !== '' && !knownFields.includes(field)) {
      delta[field] = value;
    }
  }
  return delta as FormContextDelta;
}

export interface UsePositionDetailReturn {
  position: JobPosition | null;
  setPosition: React.Dispatch<React.SetStateAction<JobPosition | null>>;
  postings: JobPosting[];
  setPostings: React.Dispatch<React.SetStateAction<JobPosting[]>>;
  applications: Application[];
  applicationsLoading: boolean;
  refetchApplications: () => Promise<void>;
  patchApplications: (updates: Application[] | ((prev: Application[]) => Application[])) => void;
  appendManualApplication: (positionId: string, applicationId: string) => Promise<void>;
  allMembers: TeamMember[];
  memberSearch: string;
  setMemberSearch: (v: string) => void;
  teamAutoMembers: string[];
  setTeamAutoMembers: React.Dispatch<React.SetStateAction<string[]>>;
  creatorUserId: string | null;
  // Posting dialogs
  postingDialog: boolean;
  setPostingDialog: (v: boolean) => void;
  newPostingChannel: Channel;
  setNewPostingChannel: (v: Channel) => void;
  newPostingTitle: string;
  setNewPostingTitle: (v: string) => void;
  handleCreatePosting: () => Promise<void>;
  canCreatePosting: boolean;
  // Edit posting
  editPosting: JobPosting | null;
  setEditPosting: (v: JobPosting | null) => void;
  editChannel: Channel;
  setEditChannel: (v: Channel) => void;
  editTitle: string;
  setEditTitle: (v: string) => void;
  handleSaveEditPosting: () => Promise<void>;
  handleTogglePosting: (postingId: string) => Promise<void>;
  // Edit position
  editOpen: boolean;
  setEditOpen: (v: boolean) => void;
  editData: Partial<JobPosition>;
  setEditData: React.Dispatch<React.SetStateAction<Partial<JobPosition>>>;
  saving: boolean;
  handleSavePosition: () => Promise<void>;
  // Sprint 3 (S3.F) — header lifecycle actions
  handleToggleActive: () => Promise<void>;
  handleSoftDelete: () => Promise<boolean>;
  handlePermanentDelete: () => Promise<boolean>;
  // Version view
  versionViewOpen: boolean;
  setVersionViewOpen: (v: boolean) => void;
  selectedVersion: PositionVersion | null;
  setSelectedVersion: (v: PositionVersion | null) => void;
  // Company data
  companyOffices: string[];
  companyTeams: CompanyTeam[];
  channels: Channel[];
  // AI Chat (edit dialog)
  aiChatOpen: boolean;
  setAiChatOpen: (v: boolean) => void;
  aiMessages: AiChatMessage[];
  aiInput: string;
  setAiInput: (v: string) => void;
  aiLoading: boolean;
  sendAiMessage: () => Promise<void>;
  // AI Auto-Fill
  autoFillReady: boolean;
  autoFillLoading: boolean;
  autoFillSuggestions: AiAutoFillSuggestions | null;
  triggerAutoFill: () => Promise<void>;
  applyAutoFillSuggestions: () => void;
  dismissAutoFillSuggestions: () => void;
  // Floating AI Chat (page-level)
  floatingChatOpen: boolean;
  setFloatingChatOpen: (v: boolean) => void;
  floatingChatMessages: AiChatMessage[];
  floatingChatInput: string;
  setFloatingChatInput: (v: string) => void;
  floatingChatLoading: boolean;
  floatingChatExpanded: boolean;
  setFloatingChatExpanded: (v: boolean) => void;
  sendFloatingChat: (overrideMessage?: string) => Promise<void>;
  resetFloatingChat: () => void;
}

export function usePositionDetail(posId: string | undefined): UsePositionDetailReturn {
  const { selectedCompany, user } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const companyId = selectedCompany?.id;

  const positionQuery = usePositionQuery(companyId, posId);
  const postingsQuery = usePositionPostingsQuery(companyId, posId);
  const position = positionQuery.data ?? null;

  const {
    applications,
    isLoading: companyApplicationsLoading,
  } = usePositionApplicationsFromCache(companyId, posId, {
    positionTitle: position?.title,
  });
  const companyTeamsQuery = useCompanyTeamsQuery(companyId);
  // Sprint ai_chat_ids_hide_and_db_complete bugfix (26/05/26 20:00):
  // exposes the company-wide positions count to the AI chat so that
  // PositionDetailPage (which scopes the prompt to a single position)
  // can still answer "how many positions do I have?" accurately.
  // Reuses the shared positions cache — no extra round trip when the
  // recruiter has already loaded /job-positions.
  const allPositionsQuery = usePositionsQuery(companyId);
  const companyPositionsTotal = allPositionsQuery.data?.length;

  const postings = postingsQuery.data ?? [];
  const applicationsLoading = !!companyId && !!posId && companyApplicationsLoading;
  const companyTeams = companyTeamsQuery.data ?? [];

  const setPosition = useCallback((
    updater: React.SetStateAction<JobPosition | null>,
  ) => {
    if (!companyId || !posId) return;
    setPositionCache(queryClient, companyId, posId, (prev) =>
      typeof updater === 'function' ? updater(prev ?? null) : updater,
    );
  }, [companyId, posId, queryClient]);

  const setPostings = useCallback((
    updater: React.SetStateAction<JobPosting[]>,
  ) => {
    if (!companyId || !posId) return;
    queryClient.setQueryData<JobPosting[]>(
      queryKeys.positionPostings(companyId, posId),
      (prev = []) => (typeof updater === 'function' ? updater(prev) : updater),
    );
  }, [companyId, posId, queryClient]);
  const [allMembers, setAllMembers] = useState<TeamMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [teamAutoMembers, setTeamAutoMembers] = useState<string[]>([]);
  const [creatorUserId, setCreatorUserId] = useState<string | null>(null);
  const [postingDialog, setPostingDialog] = useState(false);
  const [newPostingChannel, setNewPostingChannel] = useState<Channel>('Other');
  const [newPostingTitle, setNewPostingTitle] = useState('');
  const [editPosting, setEditPosting] = useState<JobPosting | null>(null);
  const [editChannel, setEditChannel] = useState<Channel>('Other');
  const [editTitle, setEditTitle] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<JobPosition>>({});
  const [saving, setSaving] = useState(false);
  const [versionViewOpen, setVersionViewOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<PositionVersion | null>(null);

  // AI Chat state (transient UI)
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [floatingChatInput, setFloatingChatInput] = useState('');
  const [floatingChatLoading, setFloatingChatLoading] = useState(false);
  const floatingChatGenRef = useRef(0);

  // Persistent chat sessions via store
  const setJobPositionSession = useAiChatStore(s => s.setJobPositionSession);
  const clearJobPositionSession = useAiChatStore(s => s.clearJobPositionSession);
  const setSession = useAiChatStore(s => s.setSession);

  const editSessionKey = useMemo(() => `job_position_${posId ?? 'new'}`, [posId]);
  const floatingSessionKey = useMemo(() => `floating_position_${posId ?? 'unknown'}`, [posId]);

  // Direct reactive selectors — re-render when store hydrates from localStorage on refresh
  const rawEditSession = useAiChatStore(s => s.jobPositionSessions[editSessionKey] ?? null);
  const editChatSession = rawEditSession && !isSessionExpired(rawEditSession.lastInteractionAt)
    ? rawEditSession
    : EMPTY_JOB_POSITION_SESSION;
  const aiMessages = editChatSession.messages;
  const conversationState = editChatSession.conversationState;
  const autoFillReady = editChatSession.autoFillReady;
  const autoFillSuggestions = editChatSession.autoFillSuggestions;

  const rawFloatingSession = useAiChatStore(s => s.sessions[floatingSessionKey] ?? null);
  const floatingSession = rawFloatingSession && !isSessionExpired(rawFloatingSession.lastInteractionAt)
    ? rawFloatingSession
    : EMPTY_SESSION;
  const floatingChatMessages = floatingSession.messages;
  const floatingConversationState = floatingSession.conversationState ?? undefined;
  const floatingChatOpen = floatingSession.isOpen;
  const floatingChatExpanded = floatingSession.isExpanded;

  const setFloatingChatOpen = useCallback((v: boolean) => {
    setSession(floatingSessionKey, { isOpen: v });
  }, [floatingSessionKey, setSession]);

  const setFloatingChatExpanded = useCallback((v: boolean) => {
    setSession(floatingSessionKey, { isExpanded: v });
  }, [floatingSessionKey, setSession]);

  const companyOffices = selectedCompany?.localization?.length
    ? selectedCompany.localization.map(l => l.address)
    : [];

  // When edit dialog closes: hide panel + clear input only (session persists in store)
  useEffect(() => {
    if (!editOpen) {
      setAiChatOpen(false);
      setAiInput('');
      setAutoFillLoading(false);
    }
  }, [editOpen]);

  // Clear floating input when closed (messages persist in store)
  useEffect(() => {
    if (!floatingChatOpen) setFloatingChatInput('');
  }, [floatingChatOpen]);

  useEffect(() => {
    if (!companyId) return;
    void api.team.list(companyId).then(members => {
      setAllMembers(members);
      const creatorMember = user ? members.find(m => m.userId === user.id || m.email === user.email) : null;
      setCreatorUserId(creatorMember?.userId || null);
    });
  }, [companyId, user]);

  const listSync = useMemo(() => createApplicationListSync(queryClient), [queryClient]);

  const refetchApplications = useCallback(async () => {
    if (!companyId) return;
    await Promise.all([
      posId ? listSync.invalidatePosition(companyId, posId) : Promise.resolve(),
      listSync.refetchAllCompanyScopes(companyId),
    ]);
  }, [companyId, posId, listSync]);

  const patchApplications = useCallback((
    updates: Application[] | ((prev: Application[]) => Application[]),
  ) => {
    if (!companyId) return;
    if (typeof updates === 'function') {
      listSync.patchCompanyUpdater(companyId, updates, false);
      return;
    }
    listSync.patchMany(companyId, updates, false);
  }, [companyId, listSync]);

  const appendManualApplication = useCallback(async (
    positionId: string,
    applicationId: string,
  ) => {
    if (!companyId) return;
    await listSync.upsertOne(companyId, positionId, applicationId);
  }, [companyId, listSync]);

  const handleCreatePosting = useCallback(async () => {
    if (!selectedCompany || !position) return;
    if (position.status !== 'active') {
      toast.error(t('positions.mustBeActiveToPost'));
      return;
    }
    try {
      const newPosting = await api.postings.create(selectedCompany.id, position.id, {
        channel: newPostingChannel,
        title: newPostingTitle || undefined,
      });
      setPostings(prev => [...prev, newPosting]);
      setPostingDialog(false);
      toast.success('Posting created successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create posting');
    }
  }, [selectedCompany, position, newPostingChannel, newPostingTitle, t]);

  const handleSaveEditPosting = useCallback(async () => {
    if (!selectedCompany || !position || !editPosting) return;
    try {
      const updated = await api.postings.update(selectedCompany.id, position.id, editPosting.id, {
        channel: editChannel,
        title: editTitle || undefined,
      });
      setPostings(prev => prev.map(p => (
        p.id === editPosting.id ? { ...updated, isActive: p.isActive } : p
      )));
      setEditPosting(null);
      toast.success(t('postings.postingUpdated'));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update posting');
    }
  }, [selectedCompany, position, editPosting, editChannel, editTitle, t]);

  const handleTogglePosting = useCallback(async (postingId: string) => {
    if (!selectedCompany || !position) return;
    const current = postings.find(p => p.id === postingId);
    if (!current) return;
    try {
      const updated = current.isActive
        ? await api.postings.closePosting(selectedCompany.id, position.id, postingId)
        : await api.postings.reopenPosting(selectedCompany.id, position.id, postingId);
      setPostings(prev => prev.map(post => post.id === postingId ? updated : post));
      toast.success(updated.isActive ? t('positions.postingActivated') : t('positions.postingDeactivated'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : undefined;
      toast.error(msg || t('postings.statusUpdateFailed'));
    }
  }, [selectedCompany, position, postings, t]);

  const handleSavePosition = useCallback(async () => {
    if (!selectedCompany || !position) return;
    setSaving(true);
    try {
      const payload: Partial<JobPosition> = { ...editData, location: normalizeJobLocation(editData.location) };
      const members = editData.assignedMembers ?? position.assignedMembers;
      if (members !== undefined) {
        payload.assignedMembers = members;
      } else {
        delete payload.assignedMembers;
      }
      const saved = await api.positions.update(selectedCompany.id, position.id, payload);
      const refreshed = await api.positions.get(selectedCompany.id, position.id);
      const nextPosition = refreshed ?? saved;
      setPosition(nextPosition);
      setPositionsCache(queryClient, selectedCompany.id, (prev) =>
        prev.map((p) => (p.id === nextPosition.id ? nextPosition : p)),
      );
      setEditOpen(false);
      toast.success(t('positions.positionUpdated'));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [selectedCompany, position, editData, t, queryClient]);

  // Sprint 3 (S3.F) — lifecycle handlers wired into the detail page header.
  // Mirrors the same API the list page uses so the two surfaces behave
  // identically; we keep the source of truth on `api.positions.*`.

  /**
   * handleToggleActive — flips DRAFT/CLOSED → ACTIVE or ACTIVE → CLOSED.
   * Updates the local `position` state optimistically and emits a toast.
   * Callers (the dropdown menu) decide which transition to fire based on
   * the current status. 402 errors are absorbed silently — the upgrade
   * wall opens via httpClient.
   */
  const handleToggleActive = useCallback(async () => {
    if (!selectedCompany || !position) return;
    const next = position.status === 'active' ? 'closed' : 'active';
    try {
      await api.positions.updateStatus(selectedCompany.id, position.id, next);
      const updated = { ...position, status: next as typeof position.status };
      setPosition(updated);
      setPositionsCache(queryClient, selectedCompany.id, (prev) =>
        prev.map((p) => (p.id === position.id ? updated : p)),
      );
      toast.success(t('positions.positionUpdated'));
    } catch (err: any) {
      if (err?.status === 402) return;
      toast.error(err?.message || 'Failed to change status');
    }
  }, [selectedCompany, position, t, queryClient]);

  /**
   * handleSoftDelete — soft-cancels the position (status=cancelled).
   * Same semantics as the trash icon on the list page. Navigates back to
   * the list so the recruiter doesn't sit on a stale detail screen.
   */
  const handleSoftDelete = useCallback(async (): Promise<boolean> => {
    if (!selectedCompany || !position) return false;
    try {
      await api.positions.delete(selectedCompany.id, position.id);
      setPositionsCache(queryClient, selectedCompany.id, (prev) =>
        prev.map((p) => (p.id === position.id ? { ...p, status: 'cancelled' } : p)),
      );
      toast.success(t('positions.positionCancelled'));
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to cancel position');
      return false;
    }
  }, [selectedCompany, position, t, queryClient]);

  /**
   * handlePermanentDelete — hard-deletes the position document and its
   * subcollections. Irrecoverable. Returns true on success so the caller
   * can navigate away.
   */
  const handlePermanentDelete = useCallback(async (): Promise<boolean> => {
    if (!selectedCompany || !position) return false;
    try {
      await api.positions.permanentDelete(selectedCompany.id, position.id);
      setPositionsCache(queryClient, selectedCompany.id, (prev) =>
        prev.filter((p) => p.id !== position.id),
      );
      toast.success(t('positions.positionDeleted'));
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete position');
      return false;
    }
  }, [selectedCompany, position, t, queryClient]);

  // AI Chat
  const sendAiMessage = useCallback(async () => {
    if (!aiInput.trim() || !selectedCompany) return;
    const userMessage = aiInput.trim();
    const now = new Date().toISOString();
    setJobPositionSession(editSessionKey, {
      messages: [...aiMessages, { role: 'user', text: userMessage }],
      lastInteractionAt: now,
    });
    setAiInput('');
    setAiLoading(true);
    try {
      const knownFields = conversationState?.knownFormFields ?? [];
      const formContextDelta = buildFormContextDelta(editData, knownFields);
      const newKnownFields = [...knownFields, ...Object.keys(formContextDelta)];

      const response = await api.ai.chat(selectedCompany.id, {
        profileName: 'job_position_assistant',
        message: userMessage,
        conversationState,
        context: Object.keys(formContextDelta).length > 0
          ? { formContext: formContextDelta }
          : undefined,
      });

      setJobPositionSession(editSessionKey, {
        messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: response.text }],
        conversationState: { ...response.conversationState, knownFormFields: newKnownFields },
        autoFillReady: autoFillReady || !!response.artifacts?.autoFillReady,
        lastInteractionAt: new Date().toISOString(),
      });
      emitBillingUsageChanged();
      if (response.statusMessages.length > 0) {
        console.debug('[AI Assistant] status:', response.statusMessages);
      }
    } catch (err: any) {
      const errorText = err?.status === 402
        ? t('billing.aiNotEnabled')
        : err?.status === 400
        ? 'Invalid request. Please try again.'
        : err?.message?.includes('demo')
        ? 'AI features are not available in demo mode.'
        : 'Something went wrong. Please try again.';
      setJobPositionSession(editSessionKey, {
        messages: [...aiMessages, { role: 'user', text: userMessage }, { role: 'ai', text: errorText }],
        lastInteractionAt: new Date().toISOString(),
      });
    } finally {
      setAiLoading(false);
    }
  }, [aiInput, selectedCompany, editData, conversationState, editSessionKey, aiMessages, autoFillReady, setJobPositionSession, t]);

  const triggerAutoFill = useCallback(async () => {
    if (!conversationState || !selectedCompany) return;
    setAutoFillLoading(true);
    try {
      const response = await api.ai.autofill(selectedCompany.id, {
        profileName: 'job_position_autofill',
        conversationState,
      });
      setJobPositionSession(editSessionKey, { autoFillSuggestions: response.suggestions });
      emitBillingUsageChanged();
    } catch (err: any) {
      const errorText = err?.status === 402
        ? t('billing.aiNotEnabled')
        : err?.message?.includes('demo')
        ? 'AI features are not available in demo mode.'
        : 'Auto-fill failed. Please try again.';
      toast.error(errorText);
    } finally {
      setAutoFillLoading(false);
    }
  }, [selectedCompany, conversationState, editSessionKey, setJobPositionSession, t]);

  const applyAutoFillSuggestions = useCallback(() => {
    if (!autoFillSuggestions) return;
    setEditData(prev => ({
      ...prev,
      ...(autoFillSuggestions.title       ? { title: autoFillSuggestions.title } : {}),
      ...(autoFillSuggestions.description ? { description: autoFillSuggestions.description } : {}),
      ...(autoFillSuggestions.seniority   ? { seniority: autoFillSuggestions.seniority } : {}),
      ...(autoFillSuggestions.contractType ? { contractType: autoFillSuggestions.contractType } : {}),
      ...(autoFillSuggestions.workMode    ? { workMode: autoFillSuggestions.workMode as any } : {}),
      ...(normalizeJobLocation(autoFillSuggestions.location)
        ? { location: normalizeJobLocation(autoFillSuggestions.location) }
        : {}),
      ...(autoFillSuggestions.salaryMin   != null ? { salaryMin: autoFillSuggestions.salaryMin } : {}),
      ...(autoFillSuggestions.salaryMax   != null ? { salaryMax: autoFillSuggestions.salaryMax } : {}),
      ...(autoFillSuggestions.salaryCurrency ? { salaryCurrency: autoFillSuggestions.salaryCurrency } : {}),
      ...(autoFillSuggestions.budget      != null ? { budget: autoFillSuggestions.budget } : {}),
      ...(autoFillSuggestions.budgetCurrency ? { budgetCurrency: autoFillSuggestions.budgetCurrency } : {}),
      ...(autoFillSuggestions.notifyCandidate != null ? { notifyCandidate: autoFillSuggestions.notifyCandidate } : {}),
      ...('allowDuplicateApplications' in (autoFillSuggestions as any) ? { allowDuplicateApplications: (autoFillSuggestions as any).allowDuplicateApplications } : {}),
    }));
    setJobPositionSession(editSessionKey, { autoFillSuggestions: null });
  }, [autoFillSuggestions, editSessionKey, setJobPositionSession]);

  const dismissAutoFillSuggestions = useCallback(() => {
    setJobPositionSession(editSessionKey, { autoFillSuggestions: null });
  }, [editSessionKey, setJobPositionSession]);

  // Floating chat send
  const sendFloatingChat = useCallback(async (overrideMessage?: string) => {
    const message = (overrideMessage ?? floatingChatInput).trim();
    if (!message || !selectedCompany) return;

    const gen = ++floatingChatGenRef.current;
    const now = new Date().toISOString();
    setSession(floatingSessionKey, {
      messages: [...floatingChatMessages, { role: 'user', text: message }],
      lastInteractionAt: now,
    });
    setFloatingChatInput('');
    setFloatingChatLoading(true);

    try {
      // Sprint ai_assistant_scope_fix (26/05/26). Symptom: the floating
      // chat in PositionDetailPage was using `job_position_assistant`,
      // which is meant for the create/edit dialog sidebar and EXPLICITLY
      // excludes candidate data from its scope. Recruiters opening the
      // chat here actually want to query candidates of THIS position
      // ("how many?", "top 3?", "rejection rate?") AND review the JD on
      // demand. Fix: switch to `applications_assistant` with a context
      // filtered to this single position + a `currentPosition` payload
      // that lets the BE prompt also support JD review/improvement.
      //
      // Sprint ai_chat_ids_hide_and_db_complete bugfix (26/05/26 15:55):
      // override candidatesTotalInDB with the denormalized
      // `position.applicationsCount` when available — this is the
      // authoritative count maintained by the BE on every application
      // create/delete (counters service). Defends against any future
      // FE-side pagination of `applications` that would underreport the
      // total to the AI prompt. Today applications is already the full
      // set (api.applications.listByPosition loops cursor since 26/05/26),
      // but the override keeps the prompt accurate even if that
      // changes.
      let applicationsContext: ReturnType<typeof buildApplicationsContext> | undefined;
      if (position) {
        const base = buildApplicationsContext(
          selectedCompany.id,
          applications,
          [position],
          { positionId: position.id },
          buildCurrentPositionContext(position),
          // Bugfix (26/05/26 20:00): forward the company-wide positions
          // counter so the AI does not reply "1 position total" from a
          // single-position scope. Falls back to 1 when the query has
          // not yet resolved — the BE prompt treats that as "the visible
          // single position" and still annotates correctly.
          { positionsTotalInDB: companyPositionsTotal ?? 1 },
        );
        applicationsContext = {
          ...base,
          candidatesTotalInDB:
            position.applicationsCount ?? base.candidatesTotalInDB,
        };
      }

      const response = await api.ai.chat(selectedCompany.id, {
        profileName: 'applications_assistant',
        message,
        conversationState: floatingConversationState,
        context: applicationsContext ? { applicationsContext } : undefined,
      });

      if (gen !== floatingChatGenRef.current) return;
      setSession(floatingSessionKey, {
        messages: [...floatingChatMessages, { role: 'user', text: message }, { role: 'ai', text: response.text }],
        conversationState: response.conversationState,
        lastInteractionAt: new Date().toISOString(),
      });
      emitBillingUsageChanged();
    } catch (err: unknown) {
      if (gen !== floatingChatGenRef.current) return;

      // Sprint 2 (S2.F) — speak in the chat itself when the BE blocks the
      // request with 402. `httpClient.ts` already dispatched
      // `hiromatch:upgrade-wall`, so the modal is being opened in parallel
      // — we just need to make sure the chat shows a meaningful AI bubble
      // instead of the generic "Something went wrong" copy that confused
      // users on the Free plan.
      const apiErr = err instanceof ApiError ? err : null;
      const billingData = (apiErr?.data ?? null) as { reason?: string } | null;
      let errorText: string;
      if (apiErr?.status === 402 && billingData?.reason === 'plan_required') {
        errorText = t('aiChat.plan_required');
      } else if (apiErr?.status === 402 && billingData?.reason === 'insufficient_balance') {
        errorText = t('aiChat.balance_exhausted');
      } else {
        const errorMessage = err instanceof Error ? err.message : '';
        errorText = errorMessage.includes('demo')
          ? t('aiChat.demo_unavailable')
          : t('aiChat.generic_error');
      }

      setSession(floatingSessionKey, {
        messages: [...floatingChatMessages, { role: 'user', text: message }, { role: 'ai', text: errorText }],
        lastInteractionAt: new Date().toISOString(),
      });
    } finally {
      if (gen === floatingChatGenRef.current) setFloatingChatLoading(false);
    }
  }, [floatingChatInput, selectedCompany, position, applications, companyPositionsTotal, floatingConversationState, floatingSessionKey, floatingChatMessages, setSession, t]);

  const resetFloatingChat = useCallback(() => {
    floatingChatGenRef.current++;
    setFloatingChatLoading(false);
    setSession(floatingSessionKey, { messages: [], conversationState: null, lastInteractionAt: null });
    setFloatingChatInput('');
  }, [floatingSessionKey, setSession]);

  return {
    position,
    setPosition,
    postings,
    setPostings,
    applications,
    applicationsLoading,
    refetchApplications,
    patchApplications,
    appendManualApplication,
    allMembers,
    memberSearch,
    setMemberSearch,
    teamAutoMembers,
    setTeamAutoMembers,
    creatorUserId,
    postingDialog,
    setPostingDialog,
    newPostingChannel,
    setNewPostingChannel,
    newPostingTitle,
    setNewPostingTitle,
    handleCreatePosting,
    canCreatePosting: position?.status === 'active',
    editPosting,
    setEditPosting,
    editChannel,
    setEditChannel,
    editTitle,
    setEditTitle,
    handleSaveEditPosting,
    handleTogglePosting,
    editOpen,
    setEditOpen,
    editData,
    setEditData,
    saving,
    handleSavePosition,
    // Sprint 3 (S3.F)
    handleToggleActive,
    handleSoftDelete,
    handlePermanentDelete,
    versionViewOpen,
    setVersionViewOpen,
    selectedVersion,
    setSelectedVersion,
    companyOffices,
    companyTeams,
    channels: CHANNELS,
    // AI Chat (edit dialog)
    aiChatOpen,
    setAiChatOpen,
    aiMessages,
    aiInput,
    setAiInput,
    aiLoading,
    sendAiMessage,
    autoFillReady,
    autoFillLoading,
    autoFillSuggestions,
    triggerAutoFill,
    applyAutoFillSuggestions,
    dismissAutoFillSuggestions,
    // Floating AI Chat
    floatingChatOpen,
    setFloatingChatOpen,
    floatingChatMessages,
    floatingChatInput,
    setFloatingChatInput,
    floatingChatLoading,
    floatingChatExpanded,
    setFloatingChatExpanded,
    sendFloatingChat,
    resetFloatingChat,
  };
}